import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  Home, ChevronRight, BarChart2, Users, Briefcase, TrendingUp,
  Globe, Award, CheckCircle2, Send, User, Mail, Phone, GraduationCap, ChevronLeft, Loader2, ShieldCheck
} from 'lucide-react';
import { fetchTestimoni, getImageUrl, submitTracerStudy, fetchTracerStats, fetchTracerStories } from '../utils/api';

const STATS = [
  { val: '87%', label: 'Tingkat Keterserapan Kerja', desc: 'Lulusan terserap dalam <6 bulan setelah wisuda', icon: <Briefcase size={22} />, color: 'blue' },
  { val: '4.5 bln', label: 'Rata-rata Waktu Tunggu', desc: 'Rata-rata waktu mendapatkan pekerjaan pertama', icon: <TrendingUp size={22} />, color: 'green' },
  { val: '92%', label: 'Bekerja Sesuai Bidang', desc: 'Alumni bekerja sesuai bidang ilmu kesehatan', icon: <Award size={22} />, color: 'purple' },
  { val: 'Rp 4,2 jt', label: 'Rata-rata Gaji Pertama', desc: 'Rata-rata penghasilan pertama lulusan STIKES HI', icon: <BarChart2 size={22} />, color: 'orange' },
];

const SECTORS = [
  { label: 'Rumah Sakit & Klinik', pct: 42, color: 'bg-blue-500' },
  { label: 'Puskesmas & FKTP', pct: 23, color: 'bg-green-500' },
  { label: 'Apotek & Farmasi', pct: 15, color: 'bg-orange-500' },
  { label: 'Industri Kesehatan', pct: 10, color: 'bg-purple-500' },
  { label: 'Instansi Pemerintah', pct: 7, color: 'bg-red-400' },
  { label: 'Lainnya', pct: 3, color: 'bg-gray-400' },
];

const FALLBACK_ALUMNI_STORIES = [
  { name: 'Ns. Rina Wulandari, S.Kep', tahun: 'Alumni 2019', prodi: 'S1 Ilmu Keperawatan', jabatan: 'Perawat Ruang ICU', quote: 'Pendidikan di STIKES HI memberikan fondasi klinis yang kuat. Saya langsung diterima kerja 2 bulan setelah lulus.', initials: 'RW', color: '#2563EB', foto: null },
  { name: 'apt. Dicky Saputra, S.Farm', tahun: 'Alumni 2020', prodi: 'S1 Farmasi', jabatan: 'Apoteker Penanggungjawab', quote: 'Program Profesi Apoteker di sini sangat komprehensif. Supervisor PKPA saya sangat mendukung dan berpengalaman.', initials: 'DS', color: '#7B2FBE', foto: null },
  { name: 'Mega Puspitasari, SKM', tahun: 'Alumni 2021', prodi: 'S1 Kesehatan Masyarakat', jabatan: 'Epidemiolog Kesehatan', quote: 'Ilmu epidemiologi yang saya pelajari langsung applicable. Sekarang saya ikut terlibat dalam surveilans penyakit menular di provinsi.', initials: 'MP', color: '#16A34A', foto: null },
];

const PRODI_MAP = {
  's1_kesehatan_masyarakat': { label: 'S1 Kesehatan Masyarakat', color: '#16A34A' },
  's1_ilmu_keperawatan':     { label: 'S1 Ilmu Keperawatan',     color: '#2563EB' },
  's1_keperawatan':          { label: 'S1 Ilmu Keperawatan',     color: '#2563EB' },
  's1_farmasi':              { label: 'S1 Farmasi',              color: '#7B2FBE' },
  'profesi_ners':            { label: 'Profesi Ners',            color: '#0D9488' },
  'profesi_apoteker':        { label: 'Profesi Apoteker',        color: '#6B7280' },
};

const COLOR_MAP = {
  blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', icon: 'text-blue-500' },
  green: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', icon: 'text-green-500' },
  purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-[#7B2FBE] dark:text-purple-400', icon: 'text-[#7B2FBE]' },
  orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400', icon: 'text-orange-500' },
};

const PRODI_OPTIONS = ['S1 Kesehatan Masyarakat', 'S1 Keperawatan', 'S1 Farmasi', 'Profesi Ners', 'Profesi Apoteker'];
const TAHUN_OPTIONS = Array.from({ length: 15 }, (_, i) => String(2025 - i));
const STATUS_OPTIONS = ['Bekerja (Sesuai Bidang)', 'Bekerja (Tidak Sesuai Bidang)', 'Wirausaha/Mandiri', 'Lanjut Studi', 'Mencari Kerja', 'Lainnya'];

const TracerStudy = () => {
  const [form, setForm] = useState({ nama: '', email: '', telepon: '', prodi: '', tahunLulus: '', status: '', instansi: '', jabatan: '', gaji: '', pesan: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(STATS);
  const [alumniStories, setAlumniStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    const interval = setInterval(() => emblaApi && emblaApi.scrollNext(), 5000);
    return () => { clearInterval(interval); emblaApi.off('select', onSelect); };
  }, [emblaApi, alumniStories]);

  useEffect(() => {
    fetchTracerStats()
      .then((res) => {
        if (res?.ok && res.stats) {
          setStats([
            { val: res.stats.serapanKerjaPct || '87%', label: 'Tingkat Keterserapan Kerja', desc: 'Lulusan terserap dalam <6 bulan setelah wisuda', icon: <Briefcase size={22} />, color: 'blue' },
            { val: res.stats.waktuTungguRataRata || '3.8 bln', label: 'Rata-rata Waktu Tunggu', desc: 'Rata-rata waktu mendapatkan pekerjaan pertama', icon: <TrendingUp size={22} />, color: 'green' },
            { val: res.stats.kesesuaianBidangPct || '92%', label: 'Bekerja Sesuai Bidang', desc: 'Alumni bekerja sesuai bidang ilmu kesehatan', icon: <Award size={22} />, color: 'purple' },
            { val: res.stats.rataRataGaji || 'Rp 4,5 jt', label: 'Rata-rata Gaji Pertama', desc: 'Rata-rata penghasilan pertama lulusan STIKES HI', icon: <BarChart2 size={22} />, color: 'orange' },
          ]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchTracerStories()
      .then((res) => {
        if (res?.ok && res.stories?.length > 0) {
          const mapped = res.stories.map((s) => ({
            name: s.name,
            prodi: s.program,
            jabatan: s.role || '',
            tahun: s.year ? `Alumni ${s.year}` : '',
            quote: s.quote,
            initials: s.name ? s.name.substring(0, 2).toUpperCase() : 'AL',
            color: '#7B2FBE',
            foto: null,
          }));
          setAlumniStories(mapped);
          setLoadingStories(false);
          return;
        }
        return fetchTestimoni().then((data) => {
          const mapped = data.map((item) => {
            const prodi = PRODI_MAP[item.prodi] || { label: item.prodi || '', color: '#7B2FBE' };
            return {
              name: item.nama,
              prodi: prodi.label,
              jabatan: item.jabatan || '',
              tahun: item.tahun_lulus ? `Alumni ${item.tahun_lulus}` : '',
              quote: item.testimoni,
              initials: item.nama ? item.nama.substring(0, 2).toUpperCase() : '??',
              color: prodi.color,
              foto: item.foto || null,
            };
          });
          const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5).slice(0, 6);
          setAlumniStories(mapped.length > 0 ? shuffle(mapped) : FALLBACK_ALUMNI_STORIES);
          setLoadingStories(false);
        });
      })
      .catch(() => {
        setAlumniStories(FALLBACK_ALUMNI_STORIES);
        setLoadingStories(false);
      });
  }, []);

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nama || !form.email || !form.prodi || !form.tahunLulus || !form.status) {
      setError('Mohon lengkapi field yang wajib diisi (*).');
      return;
    }
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
    if (siteKey && !turnstileToken) {
      setError('Harap selesaikan verifikasi keamanan Turnstile di bawah.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await submitTracerStudy({ ...form, turnstileToken });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Gagal mengirim data tracer study. Silakan periksa koneksi Anda.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tracer-study-page">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <span className="text-white/70">Akademik</span>
              <ChevronRight size={14} />
              <span className="text-white">Tracer Study</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Tracer Study Alumni</h1>
            <p className="text-white/80 max-w-xl text-base">
              Studi penelusuran lulusan untuk mengetahui rekam jejak alumni dan meningkatkan kualitas lulusan STIKES HI Jambi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">

        {/* Tautan ke unit pengelola */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-purple-100 dark:border-purple-900/30 bg-white dark:bg-[#1A182E] px-5 py-4">
            <p className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-300">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">
                <Briefcase size={17} />
              </span>
              Tracer Study dikelola oleh <strong className="text-[#1A1A2E] dark:text-white">Unit Pusat Karir</strong> STIKES HI Jambi.
            </p>
            <Link
              to="/unit/pusat-karir"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-purple-200 dark:border-purple-800 px-4 py-2 text-sm font-semibold text-[#7B2FBE] dark:text-purple-300 transition hover:border-[#7B2FBE] hover:bg-purple-50 dark:hover:bg-purple-900/20"
            >
              Kunjungi Pusat Karir <ChevronRight size={15} />
            </Link>
          </div>
        </div>

        {/* Stats */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-10">
            <p className="text-xs text-[#7B2FBE] font-semibold uppercase tracking-widest mb-2">Data Tracer Study 2023</p>
            <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Statistik Lulusan</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {stats.map((s, i) => {
              const c = COLOR_MAP[s.color];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`stat-card-${i}`}
                  className={`${c.bg} rounded-2xl p-6 text-center border border-transparent`}
                >
                  <div className={`flex items-center justify-center w-12 h-12 rounded-xl bg-white dark:bg-[#1A182E] ${c.icon} mx-auto mb-4 shadow-sm`}>
                    {s.icon}
                  </div>
                  <div className={`text-3xl font-bold font-heading mb-1 ${c.text}`}>{s.val}</div>
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{s.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{s.desc}</div>
                </motion.div>
              );
            })}
          </div>

          {/* Sectors Chart */}
          <div className="bg-white dark:bg-[#1A182E] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm mb-12">
            <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading mb-6">Sebaran Sektor Kerja Alumni</h3>
            <div className="space-y-4">
              {SECTORS.map((s, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-36 text-sm text-gray-600 dark:text-gray-400 text-right flex-shrink-0">{s.label}</div>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${s.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.1 }}
                      className={`h-3 rounded-full ${s.color}`}
                    />
                  </div>
                  <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-10 flex-shrink-0">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Alumni Stories */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Cerita Alumni</h3>
              {!loadingStories && alumniStories.length > 3 && (
                <div className="hidden md:flex gap-2">
                  <button onClick={scrollPrev} className="w-10 h-10 rounded-full bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-[#7B2FBE] hover:border-[#7B2FBE] transition-colors shadow-sm">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={scrollNext} className="w-10 h-10 rounded-full bg-white dark:bg-[#1A182E] border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-[#7B2FBE] hover:border-[#7B2FBE] transition-colors shadow-sm">
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>

            {loadingStories ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[0,1,2].map(i => <div key={i} className="bg-white dark:bg-[#1A182E] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 h-52 animate-pulse" />)}
              </div>
            ) : (
              <div className="relative">
                <div className="overflow-hidden" ref={emblaRef}>
                  <div className="flex gap-5" style={{ touchAction: 'pan-y' }}>
                    {alumniStories.map((a, i) => (
                      <div key={i} data-testid={`alumni-story-${i}`} className="flex-[0_0_100%] md:flex-[0_0_calc(33.333%-14px)] min-w-0">
                        <div className="bg-white dark:bg-[#1A182E] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm flex flex-col h-full">
                          <div className="text-[#7B2FBE]/20 text-5xl font-serif leading-none mb-3">"</div>
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5 italic flex-1">"{a.quote}"</p>
                          <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                            <div className="flex items-center gap-3">
                              {a.foto
                                ? <img src={getImageUrl(a.foto, { width: 40, height: 40, quality: 80, format: 'webp', fit: 'cover' })} alt={a.name} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                : <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ backgroundColor: a.color }}>{a.initials}</div>
                              }
                              <div>
                                <p className="font-bold text-[#1A1A2E] dark:text-white text-sm font-heading">{a.name}</p>
                                {a.jabatan && <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight mt-0.5">{a.jabatan}</p>}
                                <p className="text-xs text-[#7B2FBE] dark:text-purple-400 mt-0.5">{a.prodi}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">{a.tahun}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Dots navigasi */}
                {alumniStories.length > 3 && (
                  <div className="flex justify-center gap-2 mt-5">
                    {scrollSnaps.map((_, i) => (
                      <button key={i} onClick={() => scrollTo(i)} className={`h-2 rounded-full transition-all duration-300 ${i === selectedIndex ? 'w-8 bg-[#7B2FBE]' : 'w-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'}`} />
                    ))}
                  </div>
                )}

                {/* Navigasi mobile */}
                {alumniStories.length > 1 && (
                  <div className="flex justify-center gap-6 mt-5 md:hidden">
                    <button onClick={scrollPrev} className="text-gray-400 hover:text-[#7B2FBE]"><ChevronLeft size={24} /></button>
                    <button onClick={scrollNext} className="text-gray-400 hover:text-[#7B2FBE]"><ChevronRight size={24} /></button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Form */}
        <section className="bg-white dark:bg-[#100E1C] py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <p className="text-xs text-[#7B2FBE] font-semibold uppercase tracking-widest mb-2">Partisipasi Alumni</p>
              <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-2">Isi Data Tracer Study</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Data Anda sangat berharga untuk peningkatan kualitas lulusan STIKES HI Jambi. Semua informasi dijaga kerahasiaannya.</p>
            </div>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
                data-testid="form-success"
              >
                <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 size={40} className="text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading mb-2">Terima Kasih!</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Data tracer study Anda telah berhasil terkirim. Kontribusi Anda sangat berarti bagi pengembangan STIKES HI Jambi.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="tracer-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nama Lengkap *</label>
                    <div className="relative">
                      <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input name="nama" value={form.nama} onChange={handleChange} placeholder="Nama sesuai ijazah"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email *</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input name="email" value={form.email} onChange={handleChange} type="email" placeholder="email@example.com"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Program Studi *</label>
                    <div className="relative">
                      <GraduationCap size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <select name="prodi" value={form.prodi} onChange={handleChange}
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE] appearance-none">
                        <option value="">Pilih Program Studi</option>
                        {PRODI_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Tahun Lulus *</label>
                    <select name="tahunLulus" value={form.tahunLulus} onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE] appearance-none">
                      <option value="">Pilih Tahun</option>
                      {TAHUN_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">NIM / NPM</label>
                    <input name="nim" value={form.nim || ''} onChange={handleChange} placeholder="Contoh: 201901045 (opsional)"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">No. Telepon / WhatsApp</label>
                    <div className="relative">
                      <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input name="telepon" value={form.telepon || ''} onChange={handleChange} placeholder="08xxxxxxxxxx"
                        className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Status Saat Ini *</label>
                  <select name="status" value={form.status} onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE] appearance-none">
                    <option value="">Pilih Status</option>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Instansi/Perusahaan</label>
                    <input name="instansi" value={form.instansi} onChange={handleChange} placeholder="Nama tempat bekerja"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Jabatan/Posisi</label>
                    <input name="jabatan" value={form.jabatan} onChange={handleChange} placeholder="Jabatan saat ini"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE]" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Pesan / Saran untuk Almamater</label>
                  <textarea name="pesan" value={form.pesan} onChange={handleChange} rows={3} placeholder="Ceritakan pengalaman Anda dan saran untuk STIKES HI Jambi..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1A182E] text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2FBE] resize-none" />
                </div>

                <div className="pt-2 pb-1 flex flex-col items-center sm:items-start">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                    options={{ action: 'tracer_study' }}
                    theme="auto"
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setError('');
                    }}
                    onError={() => setError('Gagal memuat verifikasi keamanan Turnstile. Harap muat ulang halaman.')}
                    onExpire={() => setTurnstileToken(null)}
                  />
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                    <ShieldCheck size={13} className="text-[#7B2FBE]" />
                    <span>Dilindungi oleh Cloudflare Turnstile</span>
                  </div>
                </div>

                {error && (
                  <div data-testid="form-error" className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl px-4 py-2.5">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-tracer-btn"
                  className="w-full flex items-center justify-center gap-2 bg-[#7B2FBE] text-white py-3 rounded-xl font-medium hover:bg-[#9333EA] transition-colors shadow-lg shadow-purple-500/25 disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {submitting ? 'Mengirim Data...' : 'Kirim Data Tracer Study'}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TracerStudy;
